import { ReactNode, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useLanguage } from '../../../../context/LanguageContext';
import ExportFieldsSelector from './ExportFieldsSelector';
import TotalCountBanner from './TotalCountBanner';

const EXPORT_KEY = 'pages.clients.queryBuilder.exportDialog';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface QueryExportDialogProps {
    open: boolean;
    defaultEmail?: string;
    totalCount?: number;
    loading?: boolean;
    error?: string | null;
    onClose: () => void;
    onConfirm: (params: { notify: string[]; fields: string[] }) => void;
}

const parseEmails = (value: string): string[] =>
    value.split(/[\n,;]+/).map(email => email.trim()).filter(Boolean);

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
            {children}
        </Box>
    );
}

function QueryExportDialog({ open, defaultEmail = '', totalCount, loading = false, error, onClose, onConfirm }: QueryExportDialogProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [emailText, setEmailText] = useState(defaultEmail);
    const [selectedFields, setSelectedFields] = useState<string[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setEmailText(defaultEmail);
        setSelectedFields([]);
        setValidationError(null);
    }, [open, defaultEmail]);

    const toggleField = (code: string) => setSelectedFields(current => (
        current.includes(code) ? current.filter(field => field !== code) : [...current, code]
    ));

    const handleConfirm = () => {
        const emails = parseEmails(emailText);
        const invalidEmail = emails.find(email => !EMAIL_PATTERN.test(email));
        if (emails.length === 0) {
            setValidationError(t(`${EXPORT_KEY}.emailRequired`));
        } else if (invalidEmail) {
            setValidationError(t(`${EXPORT_KEY}.invalidEmail`, { email: invalidEmail }));
        } else {
            setValidationError(null);
            onConfirm({ notify: emails, fields: selectedFields });
        }
    };

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {t(`${EXPORT_KEY}.title`)}
                <IconButton onClick={onClose} size="small" disabled={loading}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {typeof totalCount === 'number' && (
                        <TotalCountBanner count={totalCount} label={t(`${EXPORT_KEY}.totalCount`)} />
                    )}

                    <Alert severity="info">{t(`${EXPORT_KEY}.infoMessage`)}</Alert>

                    {(validationError || error) && <Alert severity="error">{validationError || error}</Alert>}

                    <Section title={t(`${EXPORT_KEY}.requiredFieldsTitle`)}>
                        <Typography variant="body2" color="text.secondary">{t(`${EXPORT_KEY}.requiredFieldsList`)}</Typography>
                    </Section>

                    <Section title={t(`${EXPORT_KEY}.optionalFieldsTitle`)}>
                        <ExportFieldsSelector selected={selectedFields} disabled={loading} onToggle={toggleField} />
                    </Section>

                    <Section title={t(`${EXPORT_KEY}.emailLabel`)}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={4}
                            size="small"
                            type="email"
                            value={emailText}
                            onChange={event => {
                                setEmailText(event.target.value);
                                setValidationError(null);
                            }}
                            disabled={loading}
                            error={!!validationError}
                            helperText={t(`${EXPORT_KEY}.emailHelper`)}
                        />
                    </Section>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>{t(`${EXPORT_KEY}.back`)}</Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    {t(loading ? `${EXPORT_KEY}.submitting` : `${EXPORT_KEY}.confirm`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QueryExportDialog;
